import { StoryModel } from '../models/story.model';
import { TweenParticleLookupModel } from '../models/tween-particle-lookup.model';
import { pickBy, isEmpty, get } from 'lodash';
import { ParticleIdUtils } from '../../shared/particle-id-utils';

function getParticleSystemLookups(story: StoryModel): Record<string, string> {
  if (!story.customData || !story.customData.particleSystems) {
    return {};
  }

  return story.customData.particleSystems.reduce((acc, particleSystem) => {
    const rootId = ParticleIdUtils.getParticleIdRoot(particleSystem.id);
    acc[rootId] = particleSystem.id;
    return acc;
  }, {});
}

function assignCustomDataParticleNames(story: StoryModel, newParticleIds: Record<string, string>) {
  if (!story.customData || !story.customData.particleSystems) {
    return [];
  }

  return story.customData.particleSystems.map((particleSystem) => {
    const rootId = ParticleIdUtils.getParticleIdRoot(particleSystem.id);
    return {
      ...particleSystem,
      id: newParticleIds[rootId],
    };
  });
}

function assignChapterParticleIds(chapters, newParticleIds: Record<string, string>) {
  return chapters.map((chapter) => {
    if (chapter.customData && chapter.customData.particleSystems) {
      chapter.customData.particleSystems = chapter.customData.particleSystems.map(
        (id) => newParticleIds[ParticleIdUtils.getParticleIdRoot(id)],
      );
    }
    return chapter;
  });
}

function buildLookup(chapters, particleMap): TweenParticleLookupModel {
  return chapters
    .map((c) => c.id)
    .reduce((acc, chapterId: string) => {
      const hasParticles: boolean = chapters.some(
        (chapter) =>
          chapter.id === chapterId && get(chapter, 'customData.particleSystems', []).length > 0,
      );

      if (hasParticles) {
        acc[chapterId] = particleMap;
      }
      return acc;
    }, {});
}

/**
 *  Similar to other pieces of data, we can generate unique Ids to prevent particle system Id collisions between modules.
 *  However, merging particle systems requires more work than other pieces of data.
 *
 *  Some particle systems are controlled by tweens, whose manifests are only ever seen by the engine.
 *  A tween manifest references particle systems by Id, so changing a particle system id breaks the reference.
 *
 *  Therefore, we must create a TweenParticleLookup, so the engine can locate the correct particle system given the original particle system Id,
 *  and the currently active chapter.
 *  See `tweenParticleLookup.js` in the engine.
 *
 *  Subsquent to the module merge, The TweenParticleLookup needs to be refreshed whenever a chapter Id changes, i.e. saving to a bookmark.
 *  See `refreshTweenParticleLookup`.
 *
 */
export function mergeParticleSystems(
  currentStory: StoryModel,
  importedStory: StoryModel,
  currentChapters: any[],
  importedChapters: any[],
  uuidFn,
): {
  mergedParticles: any[];
  currentChapters: any[];
  importedChapters: any[];
  tweenParticleLookup: TweenParticleLookupModel;
} {
  // Store as a lookup, so chapters can update their references to particle Ids.
  const currentStoryParticleLookups = getParticleSystemLookups(currentStory);
  const currentStoryParticleIds = Object.values(currentStoryParticleLookups);

  const importedStoryParticleLookups = getParticleSystemLookups(importedStory);

  // Update imported particle system ids only if they clash with
  // Existing particle system ids
  for (const originalId in importedStoryParticleLookups) {
    if (importedStoryParticleLookups.hasOwnProperty(originalId)) {
      const id = importedStoryParticleLookups[originalId];

      if (currentStoryParticleIds.includes(id)) {
        const newId = ParticleIdUtils.generateParticleId(originalId, uuidFn);
        importedStoryParticleLookups[originalId] = newId;
      }
    }
  }

  // apply the new particle system Ids to the module's custom data.
  const mergedParticles = [
    ...assignCustomDataParticleNames(currentStory, currentStoryParticleLookups),
    ...assignCustomDataParticleNames(importedStory, importedStoryParticleLookups),
  ];

  // update the chapter's particle system Id references
  const currentChaptersUpdatedParticles = assignChapterParticleIds(
    currentChapters,
    currentStoryParticleLookups,
  );

  const importedChaptersUpdatedParticles = assignChapterParticleIds(
    importedChapters,
    importedStoryParticleLookups,
  );

  // Build the lookup so the engine can find the right particle system.
  const tweenParticleLookup: TweenParticleLookupModel = {
    ...buildLookup(currentChaptersUpdatedParticles, currentStoryParticleLookups),
    ...buildLookup(importedChaptersUpdatedParticles, importedStoryParticleLookups),
  };

  // filter chapters that don't have a particle system.
  const filteredLookup = pickBy(tweenParticleLookup, (value, key) => !isEmpty(value));

  return {
    mergedParticles,
    currentChapters: currentChaptersUpdatedParticles,
    importedChapters: importedChaptersUpdatedParticles,
    tweenParticleLookup: filteredLookup,
  };
}
